import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Layers } from "lucide-react";
import { toast } from "sonner";

const ALL_MODULES = [
    "dashboard", "reservations", "rooms", "guests", "housekeeping",
    "pos", "channels", "reports", "audit", "billing", "settings", "booking_engine",
];

export default function SuperModules() {
    const [hotels, setHotels] = useState([]);
    const [selected, setSelected] = useState(null);
    const [modules, setModules] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/hotels").then(({ data }) => {
            setHotels(data);
            if (data[0]) setSelected(data[0]);
        });
    }, []);

    useEffect(() => {
        if (!selected) return;
        api.get(`/admin/modules/${selected.id}`).then(({ data }) => setModules(data.enabled || []));
    }, [selected]);

    const toggle = (mod) => {
        setModules((m) => m.includes(mod) ? m.filter((x) => x !== mod) : [...m, mod]);
    };

    const save = async () => {
        setSaving(true);
        try { await api.put(`/admin/modules/${selected.id}`, { enabled: modules }); toast.success("Modules updated"); }
        catch { toast.error("Failed to save"); }
        finally { setSaving(false); }
    };

    return (
        <>
            <Topbar title="Modules per hotel" subtitle="Enable / disable functionality per tenant" />
            <div className="flex flex-1 overflow-hidden">
                {/* Hotels list */}
                <div className="w-[260px] shrink-0 overflow-auto border-r border-border bg-card">
                    <div className="px-4 py-3 akm-section-title">Hotels</div>
                    <ul>
                        {hotels.map((h) => (
                            <li key={h.id}>
                                <button
                                    onClick={() => setSelected(h)}
                                    className={`flex w-full items-center gap-2 px-4 py-2 text-left text-[13px] transition-colors ${selected?.id === h.id ? "bg-accent text-accent-foreground" : "hover:bg-muted/60"}`}
                                    data-testid={`hotel-select-${h.slug}`}
                                >
                                    <Layers size={13} className="text-muted-foreground" />
                                    <span className="min-w-0 flex-1 truncate">{h.name}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Modules editor */}
                <div className="flex-1 overflow-auto p-6">
                    {selected ? (
                        <>
                            <div className="mb-4">
                                <div className="text-sm font-semibold tracking-tight">{selected.name}</div>
                                <div className="text-[11px] text-muted-foreground">{selected.slug}</div>
                            </div>
                            <div className="akm-card p-4 max-w-2xl">
                                <div className="mb-3 akm-section-title">Enabled modules</div>
                                <div className="grid grid-cols-2 gap-2">
                                    {ALL_MODULES.map((mod) => (
                                        <label key={mod} className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-[13px] capitalize transition-colors ${modules.includes(mod) ? "border-primary bg-primary/5 text-foreground" : "border-border bg-card hover:bg-muted/60"}`}>
                                            <input
                                                type="checkbox"
                                                checked={modules.includes(mod)}
                                                onChange={() => toggle(mod)}
                                                className="accent-[hsl(var(--primary))]"
                                                data-testid={`module-${mod}`}
                                            />
                                            {mod.replace("_", " ")}
                                        </label>
                                    ))}
                                </div>
                                <div className="mt-4 flex justify-end">
                                    <button onClick={save} disabled={saving} className="akm-btn-primary" data-testid="modules-save">{saving ? "Saving…" : "Save changes"}</button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="py-12 text-center text-sm text-muted-foreground">Select a hotel</div>
                    )}
                </div>
            </div>
        </>
    );
}
